import React from "react";

import {
  useEncounterOrderFile,
  useEncounterOrderFileRamsoft,
} from "modules/orders/api";

import {
  PdfViewer,
  PdfViewerActions,
  PdfViewerActivePage,
  PdfViewerContainer,
  PdfViewerHeader,
  PdfViewerPageIndicator,
  PdfViewerPreview,
  TabList,
  Tabs,
} from "shared";

import {
  Box,
  Icon,
  IconButton,
  Tab,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";
import { Collapse, Expand } from "icons";

type RadiologyChartFileProps = {
  isFileMode: boolean;
  toggleFileMode: () => void;
};

function RadiologyChartFileWrapper(
  props: RadiologyChartFileProps & { orderId: string }
) {
  const { orderId, isFileMode, toggleFileMode } = props;
  const [activeTab, setActiveTab] = React.useState(0);

  const { data: manualFile } = useEncounterOrderFile(orderId);
  const { data: ramsoftFile } = useEncounterOrderFileRamsoft(orderId);

  if (isFileMode) {
    return (
      <>
        {activeTab === 0 && (
          <RadiologyChartRamsoftFile
            file={ramsoftFile?.data}
            isFileMode={isFileMode}
            toggleFileMode={toggleFileMode}
          />
        )}
        {activeTab === 1 && (
          <RadiologyChartManualFile
            file={manualFile?.data}
            isFileMode={isFileMode}
            toggleFileMode={toggleFileMode}
          />
        )}
      </>
    );
  }

  return (
    <Tabs
      isFitted
      size="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
      index={activeTab}
      onChange={(index) => {
        setActiveTab(index);
      }}
    >
      <Box
        sx={{
          paddingTop: "5px",
          marginBottom: "20px",
        }}
      >
        <TabList
          index={activeTab}
          sx={{
            maxWidth: "70%",
            display: "flex",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <Tab
            maxH="48"
            fontSize={"1.175rem"}
            sx={{
              color: ramsoftFile?.data ? "gray" : "red",
            }}
            _selected={{
              margin: "3px",
              height: "42px",
              bgColor: ramsoftFile?.data ? "green" : "red",
              color: "white",
              borderRadius: "5px",
            }}
          >
            Ramsoft Report
          </Tab>
          <Tab
            maxH="48"
            fontSize={"1.175rem"}
            sx={{
              color: manualFile?.data ? "gray" : "red",
            }}
            _selected={{
              margin: "3px",
              height: "42px",
              bgColor: manualFile?.data ? "green" : "red",
              color: "white",
              borderRadius: "5px",
            }}
          >
            Other Documents
          </Tab>
        </TabList>
      </Box>
      <TabPanels
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <TabPanel>
          {activeTab === 0 && (
            <RadiologyChartRamsoftFile
              file={ramsoftFile?.data}
              isFileMode={isFileMode}
              toggleFileMode={toggleFileMode}
            />
          )}
        </TabPanel>
        <TabPanel>
          {activeTab === 1 && (
            <RadiologyChartManualFile
              file={manualFile?.data}
              isFileMode={isFileMode}
              toggleFileMode={toggleFileMode}
            />
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

function RadiologyChartRamsoftFile(
  props: RadiologyChartFileProps & { file: Blob | undefined }
) {
  const { file, isFileMode, toggleFileMode } = props;
  return (
    <PdfViewer value={file}>
      <PdfViewerContainer
        height={isFileMode ? "160%" : "90%"}
        marginBottom="20px"
        activePage={<PdfViewerActivePage pageWidth={isFileMode ? 1600 : 900} />}
        header={
          <PdfViewerHeader
            title="Ramsoft Report"
            leftElements={[
              <IconButton
                key="expand-collapse-button"
                aria-label="submit document"
                icon={<Icon as={isFileMode ? Collapse : Expand} />}
                color="blue"
                variant="icon"
                onClick={() => toggleFileMode()}
              />,
              <PdfViewerPageIndicator key="page-indicator" />,
            ]}
            rightElements={[
              <PdfViewerActions
                key="viewer-actions"
                showSubmit={false}
                showDelete={false}
              />,
            ]}
          />
        }
        preview={<PdfViewerPreview />}
      />
    </PdfViewer>
  );
}

function RadiologyChartManualFile(
  props: RadiologyChartFileProps & { file: Blob | undefined }
) {
  const { file, isFileMode, toggleFileMode } = props;
  return (
    <PdfViewer value={file}>
      <PdfViewerContainer
        height={isFileMode ? "160%" : "90%"}
        marginBottom="20px"
        activePage={<PdfViewerActivePage pageWidth={isFileMode ? 1600 : 900} />}
        header={
          <PdfViewerHeader
            title="Radiology Report"
            leftElements={[
              <IconButton
                key="expand-collapse-button"
                aria-label="submit document"
                icon={<Icon as={isFileMode ? Collapse : Expand} />}
                color="blue"
                variant="icon"
                onClick={() => toggleFileMode()}
              />,
              <PdfViewerPageIndicator key="page-indicator" />,
            ]}
            rightElements={[
              <PdfViewerActions
                key="viewer-actions"
                showSubmit={false}
                showDelete={false}
              />,
            ]}
          />
        }
        preview={<PdfViewerPreview />}
      />
    </PdfViewer>
  );
}

export {
  RadiologyChartFileWrapper,
  RadiologyChartRamsoftFile,
  RadiologyChartManualFile,
};
